import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AppModalService } from '../../../core/services/app-modal.service';
import {
  FinancialRow,
  IssueCard,
  IssueEditedValueDTO,
  IssueEditFieldsDTO,
} from '../../../core/interface/taxAccounting.interface';
import { AuditTrailRecordDTO } from '../../../core/interface/audit-trail.interface';
import {
  AUDIT_TRAIL_CONSTANT,
  AUDIT_TRAIL_TABLE_COLUMNS,
} from '../../../core/constants/auditTrail.constants';
import { ApiService } from '../../../core/services/app-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppCommonService } from '../../../core/services/app-common.service';
import { CancelColorDirective } from '../../../shared/directives/cancel-color.directive';

@Component({
  selector: 'app-tax-accounting-records',
  standalone: true,
  imports: [CommonModule, FormsModule, CancelColorDirective],
  templateUrl: './tax-accounting-records.html',
  styleUrl: './tax-accounting-records.scss',
})
export class TaxAccountingRecords implements OnInit, OnChanges {
  @Input() issue!: IssueCard;
  @Input() editMode = false;
  @Input() rowIndex = 0;
  @Input() tbbsStatus!: string[];
  private readonly appService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appCommonService = inject(AppCommonService);
  issueEditData!: IssueEditFieldsDTO;
  issueEditDataInitial!: IssueEditFieldsDTO;
  isAdminUser = false;
  isCancelIssue = false;
  readonly tbbsStatusOptions: string[] = ['Waiting', 'Completed'];

  constructor(private readonly appModalService: AppModalService) {}

  ngOnInit(): void {
    this.issue = structuredClone(this.issue);
    this.isAdminUser = JSON.parse(sessionStorage.getItem('hasAdministrativeAccess') ?? 'false');
    this.tbbsStatus = this.tbbsStatusOptions;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['issue']?.currentValue) {
      this.bindIssueData();
      this.isCancelIssue = this.issue.irsIssueId === 'Cancel';
    }
  }

  bindIssueData() {
    this.issueEditData = {
      ascFlag: this.issue.asC_740Flag,
      ascIssueId: this.issue.asC_740_IssueID || '',
      ascComment: this.issueRow?.asC_740_comment || '',
      tbbsFlag: this.issue.tbbsFlag,
      tbbsStatus: this.issue.tbbsStatus,
      tbbsDescription: this.issue.tbbsDescription,
      spLink: this.issue.spLink || '',
    };
    this.issueEditDataInitial = structuredClone(this.issueEditData);
  }

  hasChanges(): boolean {
    return this.getChangedFields().length > 0;
  }

  getChangedFields(): IssueEditedValueDTO[] {
    return (Object.keys(this.issueEditData) as (keyof IssueEditFieldsDTO)[])
      .filter((field) => this.issueEditData[field] !== this.issueEditDataInitial[field])
      .map((field) => ({
        field,
        oldValue: this.issueEditDataInitial[field],
        newValue: this.issueEditData[field],
      }));
  }

  get yearRow(): FinancialRow | undefined {
    return this.issue?.details?.find((row) => row.year !== undefined);
  }

  get issueRow(): FinancialRow | undefined {
    return this.issue?.details?.find((row) => row.label === 'Issue');
  }

  editRecord(): void {
    this.appModalService.TaxAccEditSaveTrigger(structuredClone(this.issue));
  }

  showAudit(): void {
    const auditID = this.issue?.id;
    this.appService
      .getAuditTrailById(auditID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const columns = AUDIT_TRAIL_TABLE_COLUMNS;
          const res = response?.result;
          const data: AuditTrailRecordDTO[] = res.map((item: any, index: number) => {
            return {
              Index: index + 1,
              'Field Name': item.fieldName,
              'Modified By': item.userName,
              DateTime: this.appCommonService.formatDisplayDateTime(item.auditDateTime),
              'Old Value': item.oldValue,
              'New Value': item.newValue,
            };
          });

          this.appModalService.openAudit(AUDIT_TRAIL_CONSTANT.auditTrail, columns, data);
        },
        error: (error) => {
          console.error('Error fetching audit trail:', error);
        },
      });
  }
}
